/**
 * WebAuthn capability detection.
 *
 * These are plain `PublicKeyCredential` feature checks that mirror
 * `browserSupportsWebAuthn()` / `platformAuthenticatorIsAvailable()` from
 * `@simplewebauthn/browser`. They live here rather than delegating to the library
 * because support is probed on app start (`initPasskeySupported$`), and loading the
 * library there would pull it in on every session instead of only when the passkey
 * flow is actually triggered.
 */

type PublicKeyCredentialStatic = {
  isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>
}

const getPublicKeyCredential = (): PublicKeyCredentialStatic | undefined => {
  const candidate = (globalThis as { PublicKeyCredential?: unknown })
    ?.PublicKeyCredential
  return typeof candidate === 'function'
    ? (candidate as unknown as PublicKeyCredentialStatic)
    : undefined
}

/** True when the browser exposes the WebAuthn API. */
export const browserSupportsWebAuthn = (): boolean =>
  getPublicKeyCredential() !== undefined

/**
 * True when the device has a built-in (platform) authenticator — Touch ID, Face ID,
 * Windows Hello — that can hold a passkey.
 */
export const platformAuthenticatorIsAvailable = (): Promise<boolean> => {
  const publicKeyCredential = getPublicKeyCredential()
  if (!publicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) {
    return Promise.resolve(false)
  }
  return publicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
}
